import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button, Empty, Progress, Spin, Tag, message } from 'antd'
import { CreditCardOutlined, ReloadOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../../hooks/AuthContext'
import { useMembership } from '../../hooks/useMembership'
import { usePermission } from '../../hooks/usePermission'
import { getApiErrorMessage } from '../../utils/api'
import { getCustomerInstallmentPlans } from '../../api/installment'
import Hint from '../common/Hint'
import InstallmentPaymentModal from './InstallmentPaymentModal'
import InstallmentDetailModal from './InstallmentDetailModal'
import {
  INSTALLMENT_FREQUENCY_LABEL,
  installmentMoney as money
} from './installmentShared'

/**
 * 某位顧客的分期：未收多少、這次來該收多少，並可直接登錄收款。
 *
 * 為什麼要有這個元件：分期原本只能在「分期收款」頁查，客人下次來店時，
 * 結帳的人（常常不是當初開分期的那位）根本不會知道這位客人還欠一期。
 * 所以把同一份資訊放到顧客資料頁與結帳頁（現場／帶入預約），在哪裡都收得到。
 *
 * variant:
 *   'checkout' —— 結帳頁：沒有進行中的分期就完全不顯示，有的話用提示框強調
 *   'customer' —— 顧客資料頁：含已結清／已取消的歷史
 *
 * 方案／權限不符時整個元件回傳 null，呼叫端不用自己判斷。
 */

const PlanRow = styled.div`
  border: 1px solid ${(p) => (p.$overdue ? '#f4c7bd' : '#ececee')};
  background: #fff;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 10px;

  .top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
  }

  .title {
    font-weight: 600;
    color: #1d1d1f;
    font-size: 14px;
  }

  .meta {
    font-size: 12px;
    color: #86868b;
    margin-top: 2px;
  }

  .amounts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    font-size: 13px;
    color: #57606a;
    margin: 8px 0;

    strong {
      color: #1d1d1f;
    }
  }

  .actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
`

const SummaryLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 13px;

  strong {
    color: #1d1d1f;
  }

  .due {
    color: #c0392b;
  }
`

const EMPTY_SUMMARY = { outstanding: 0, overdue: 0, dueNow: 0, activeCount: 0 }

const CustomerInstallmentsPanel = ({
  customerId,
  variant = 'customer',
  onChanged,
  onSummary,
  style
}) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { hasFeature } = useMembership()
  const { can, canAny } = usePermission()

  const businessId = user?.businessId
  const enabled =
    hasFeature('installment') &&
    canAny(['installment:view', 'installment:manage'])
  const canManage = can('installment:manage')
  const isCheckout = variant === 'checkout'

  const [loading, setLoading] = useState(false)
  const [plans, setPlans] = useState([])
  const [summary, setSummary] = useState(EMPTY_SUMMARY)
  const [showClosed, setShowClosed] = useState(false)
  const [payPlan, setPayPlan] = useState(null)
  const [detailPlan, setDetailPlan] = useState(null)

  const fetchPlans = useCallback(async () => {
    if (!enabled || !businessId || !customerId) {
      setPlans([])
      setSummary(EMPTY_SUMMARY)
      return
    }
    setLoading(true)
    try {
      const res = await getCustomerInstallmentPlans(businessId, customerId, {
        includeClosed: !isCheckout
      })
      const data = res?.data?.data || {}
      setPlans(data.plans || [])
      setSummary(data.summary || EMPTY_SUMMARY)
    } catch (err) {
      // 結帳頁不因為分期查不到而打斷結帳，只在顧客頁提示
      if (!isCheckout) {
        message.error(getApiErrorMessage(err, '取得分期資料失敗'))
      }
    } finally {
      setLoading(false)
    }
  }, [enabled, businessId, customerId, isCheckout])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  useEffect(() => {
    onSummary && onSummary(summary)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary])

  if (!enabled || !customerId) return null

  const activePlans = plans.filter((p) => p.status === 'active')
  const closedPlans = plans.filter((p) => p.status !== 'active')

  // 結帳頁：沒有進行中的分期就不佔版面
  if (isCheckout && activePlans.length === 0) return null

  const afterChange = () => {
    fetchPlans()
    onChanged && onChanged()
  }

  const renderPlan = (plan) => {
    const total = plan.totalWithFee || 0
    const percent = total > 0 ? Math.round(((plan.paidAmount || 0) / total) * 100) : 0
    const active = plan.status === 'active'
    return (
      <PlanRow key={plan._id} $overdue={plan.isOverdue}>
        <div className='top'>
          <div>
            <div className='title'>
              {plan.orderCode || '分期'}
              {plan.isOverdue && (
                <Tag color='red' style={{ marginLeft: 8 }}>
                  逾期 {money(plan.overdueAmount)}
                </Tag>
              )}
              {active && plan.dueNowAmount > 0 && !plan.isOverdue && (
                <Tag color='orange' style={{ marginLeft: 8 }}>
                  本次應繳 {money(plan.dueNowAmount)}
                </Tag>
              )}
              {plan.status === 'completed' && (
                <Tag color='green' style={{ marginLeft: 8 }}>已結清</Tag>
              )}
              {plan.status === 'canceled' && <Tag style={{ marginLeft: 8 }}>已取消</Tag>}
            </div>
            <div className='meta'>
              分 {plan.periods} 期 · {INSTALLMENT_FREQUENCY_LABEL[plan.frequency] || ''}
              {plan.createdAt && <> · 建立於 {String(plan.createdAt).slice(0, 10)}</>}
            </div>
          </div>
          <div style={{ minWidth: 140 }}>
            <Progress percent={percent} size='small' strokeColor='#d5728a' />
          </div>
        </div>
        <div className='amounts'>
          <span>
            總額 <strong>{money(total)}</strong>
          </span>
          <span>
            已收 <strong>{money(plan.paidAmount)}</strong>
          </span>
          <span>
            未收 <strong>{money(plan.outstanding)}</strong>
          </span>
          {active && plan.nextDueDate && (
            <span>
              下期 <strong>{plan.nextDueDate}</strong>（{money(plan.nextDueAmount)}）
            </span>
          )}
        </div>
        <div className='actions'>
          {canManage && active && (
            <Button type='primary' size='small' onClick={() => setPayPlan(plan)}>
              {plan.dueNowAmount > 0 ? `收款 ${money(plan.dueNowAmount)}` : '登錄收款'}
            </Button>
          )}
          <Button size='small' onClick={() => setDetailPlan(plan)}>
            明細
          </Button>
        </div>
      </PlanRow>
    )
  }

  const summaryLine = (
    <SummaryLine>
      <span>
        進行中 <strong>{summary.activeCount}</strong> 筆
      </span>
      <span>
        未收 <strong>{money(summary.outstanding)}</strong>
      </span>
      {summary.dueNow > 0 && (
        <span className='due'>
          本次應繳 <strong className='due'>{money(summary.dueNow)}</strong>
        </span>
      )}
      {summary.overdue > 0 && (
        <span className='due'>
          逾期 <strong className='due'>{money(summary.overdue)}</strong>
        </span>
      )}
    </SummaryLine>
  )

  const modals = (
    <>
      <InstallmentPaymentModal
        open={!!payPlan}
        plan={payPlan}
        businessId={businessId}
        onClose={() => setPayPlan(null)}
        onPaid={() => {
          setPayPlan(null)
          afterChange()
        }}
      />
      <InstallmentDetailModal
        open={!!detailPlan}
        plan={detailPlan}
        businessId={businessId}
        canManage={canManage}
        onClose={() => setDetailPlan(null)}
        onChanged={afterChange}
      />
    </>
  )

  if (isCheckout) {
    return (
      <div style={{ marginBottom: 16, ...style }}>
        <Hint
          tone={summary.overdue > 0 || summary.dueNow > 0 ? 'warning' : 'info'}
          title={
            <span>
              <CreditCardOutlined style={{ marginRight: 6 }} />
              這位顧客有 {summary.activeCount} 筆分期尚未結清
            </span>
          }
        >
          {summaryLine}
          <div style={{ fontSize: 12, color: '#86868b', marginTop: 4 }}>
            分期款與本次消費分開入帳：在這裡按「收款」會直接登錄到分期帳上，
            不要再把金額加進下方的付款欄位。
          </div>
          <Spin spinning={loading}>{activePlans.map(renderPlan)}</Spin>
        </Hint>
        {modals}
      </div>
    )
  }

  return (
    <div style={style}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap'
        }}
      >
        {summaryLine}
        <div style={{ display: 'flex', gap: 4 }}>
          <Button size='small' icon={<ReloadOutlined />} onClick={fetchPlans} />
          <Button
            size='small'
            type='link'
            onClick={() => navigate('/checkout/installments')}
          >
            分期收款頁
          </Button>
        </div>
      </div>
      <Spin spinning={loading}>
        {plans.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='這位顧客沒有分期紀錄'
            style={{ margin: '24px 0' }}
          />
        ) : (
          <>
            {activePlans.length === 0 && (
              <div style={{ fontSize: 13, color: '#86868b', marginTop: 10 }}>
                目前沒有進行中的分期
              </div>
            )}
            {activePlans.map(renderPlan)}
            {closedPlans.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Button
                  type='link'
                  size='small'
                  style={{ padding: 0 }}
                  onClick={() => setShowClosed((v) => !v)}
                >
                  {showClosed
                    ? '收起已結清／已取消'
                    : `顯示已結清／已取消（${closedPlans.length}）`}
                </Button>
                {showClosed && closedPlans.map(renderPlan)}
              </div>
            )}
          </>
        )}
      </Spin>
      {modals}
    </div>
  )
}

export default CustomerInstallmentsPanel
